import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, GraduationCap, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { BASE_URL } from '../apiConfig';
import { getTenantHeaders } from '../config/tenant';
import SEOHead from '../components/common/SEOHead';
import { BRAND_NAME, SITE_URL } from '../config/brand';
import ScrollReveal from '../components/common/ScrollReveal';

type TeacherSubject = {
  id: string;
  name: string;
  description?: string;
  code?: string;
  gradeName?: string;
  gradeId?: string;
  courseCount?: number;
};

type TeacherGrade = {
  gradeName: string;
  gradeId: string;
  subjects: TeacherSubject[];
  courseCount: number;
};

const GRADE_ORDER = [
  'الأول الإعدادي',
  'أولى إعدادي',
  'الثاني الإعدادي',
  'ثانية إعدادي',
  'الثالث الإعدادي',
  'ثالثة إعدادي',
  'الأول الثانوي',
  'أولى ثانوي',
  'الثاني الثانوي',
  'ثانية ثانوي',
  'الثالث الثانوي',
  'ثالثة ثانوي',
  'تالتة ثانوي',
];

function getGradeValue(gradeName?: string): string {
  if (!gradeName) return 'secondary1';
  if (gradeName.includes('الأول الثانوي') || gradeName.includes('أولى ثانوي')) return 'secondary1';
  if (gradeName.includes('الثاني الثانوي') || gradeName.includes('ثانية ثانوي')) return 'secondary2';
  if (gradeName.includes('الثالث الثانوي') || gradeName.includes('ثالثة ثانوي') || gradeName.includes('تالتة ثانوي')) {
    return 'secondary3';
  }
  if (gradeName.includes('الأول الإعدادي') || gradeName.includes('أولى إعدادي')) return 'prep1';
  if (gradeName.includes('الثاني الإعدادي') || gradeName.includes('ثانية إعدادي')) return 'prep2';
  if (gradeName.includes('الثالث الإعدادي') || gradeName.includes('ثالثة إعدادي')) return 'prep3';
  return 'secondary1';
}

function toGradeStateKey(gradeValue: string): string {
  const map: Record<string, string> = {
    secondary1: 'Secondary1',
    secondary2: 'Secondary2',
    secondary3: 'Secondary3',
    prep1: 'Prep1',
    prep2: 'Prep2',
    prep3: 'Prep3',
  };
  return map[gradeValue] ?? 'Secondary1';
}

function gradeSortIndex(gradeName: string): number {
  const idx = GRADE_ORDER.findIndex((label) => gradeName.includes(label) || label.includes(gradeName));
  return idx === -1 ? 999 : idx;
}

function groupSubjectsByGrade(subjects: TeacherSubject[]): TeacherGrade[] {
  const map = new Map<string, TeacherGrade>();

  subjects.forEach((subject) => {
    const gradeId = subject.gradeId || subject.gradeName || 'unknown';
    const gradeName = subject.gradeName || 'مرحلة غير محددة';
    const existing = map.get(gradeId);

    if (existing) {
      existing.subjects.push(subject);
      existing.courseCount += subject.courseCount ?? 0;
    } else {
      map.set(gradeId, {
        gradeId,
        gradeName,
        subjects: [subject],
        courseCount: subject.courseCount ?? 0,
      });
    }
  });

  return Array.from(map.values()).sort(
    (a, b) => gradeSortIndex(a.gradeName) - gradeSortIndex(b.gradeName)
  );
}

const TeacherPage: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    imagePath?: string;
    department?: string;
    biography?: string;
    yearsOfExperience?: number;
    active?: boolean;
  } | null>(null);
  const [subjects, setSubjects] = useState<TeacherSubject[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingGrades, setLoadingGrades] = useState(true);

  const grades = useMemo(() => groupSubjectsByGrade(subjects), [subjects]);

  useEffect(() => {
    const fetchTeacher = async () => {
      if (!teacherId) return;

      try {
        setLoading(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{
            id: string;
            firstName: string;
            lastName: string;
            fullName: string;
            imagePath?: string;
            department?: string;
            biography?: string;
            yearsOfExperience?: number;
            active: boolean;
          }>;
        }>(`${BASE_URL}api/Teacher`, {
          params: { active: true },
          headers: { ...getTenantHeaders() },
        });

        const found = response.data?.data?.find((t) => t.id === teacherId);
        setTeacher(
          found ?? {
            id: teacherId,
            firstName: 'مدرس',
            lastName: '',
            fullName: 'مدرس',
            active: true,
          }
        );
      } catch {
        setTeacher({
          id: teacherId,
          firstName: 'مدرس',
          lastName: '',
          fullName: 'مدرس',
          active: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTeacher();
  }, [teacherId]);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!teacherId) return;

      try {
        setLoadingGrades(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{
            id: string;
            name: string;
            description?: string;
            code?: string;
            gradeName?: string;
            gradeId?: string;
            courseCount?: number;
          }>;
        }>(`${BASE_URL}api/Teacher/${teacherId}/subjects`, {
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data.data) {
          setSubjects(
            response.data.data.map((subject) => ({
              id: subject.id,
              name: subject.name,
              description: subject.description,
              code: subject.code,
              gradeName: subject.gradeName,
              gradeId: subject.gradeId,
              courseCount: subject.courseCount,
            }))
          );
        } else {
          setSubjects([]);
        }
      } catch {
        setSubjects([]);
      } finally {
        setLoadingGrades(false);
      }
    };

    fetchSubjects();
  }, [teacherId]);

  const handleGradeClick = (grade: TeacherGrade) => {
    const gradeValue = getGradeValue(grade.gradeName);
    navigate(`/courses/grade/${gradeValue}`, {
      state: {
        grade: toGradeStateKey(gradeValue),
        teacherId,
        subjects: grade.subjects,
      },
    });
  };

  const displayTeacher = teacher ?? {
    id: teacherId || '',
    firstName: 'مدرس',
    lastName: '',
    fullName: 'مدرس',
  };

  if (loading && !teacher) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F6FA] px-4" dir="rtl">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#C5CDDC] border-t-[#001848]" />
          <p className="text-sm text-slate-600 sm:text-base">جاري تحميل بيانات المدرس...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6FA] font-cairo" dir="rtl">
      <SEOHead
        title={`${displayTeacher.fullName} | مدرس في ${BRAND_NAME}`}
        description={
          displayTeacher.biography?.trim() ||
          `تعرّف على المدرس ${displayTeacher.fullName} وكورساته على منصة أوائل مصر — دروس أونلاين.`
        }
        keywords={`${displayTeacher.fullName}, معلمون, ${BRAND_NAME}, كورسات أونلاين`}
        url={`/teacher/${teacherId || displayTeacher.id}`}
        image={displayTeacher.imagePath || '/logo.png'}
        breadcrumbs={[{ name: displayTeacher.fullName, path: `/teacher/${teacherId || displayTeacher.id}` }]}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: displayTeacher.fullName,
          description: displayTeacher.biography || `مدرس على منصة ${BRAND_NAME}`,
          url: `${SITE_URL}/teacher/${teacherId || displayTeacher.id}`,
          image: displayTeacher.imagePath || `${SITE_URL}/logo.png`,
          jobTitle: displayTeacher.department || 'مدرس',
          worksFor: {
            '@type': 'EducationalOrganization',
            name: BRAND_NAME,
            url: SITE_URL,
          },
        }}
      />

      <header className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-5xl items-center px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-[#001848] hover:text-gold-600"
          >
            <ArrowRight className="h-5 w-5" />
            العودة
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        <section>
          <ScrollReveal variant="up" className="mb-6 text-center sm:mb-8 sm:text-start">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 sm:text-sm">
              <GraduationCap className="h-3.5 w-3.5" />
              {displayTeacher.fullName}
            </div>
            <h1 className="text-xl font-bold text-[#001848] font-cairo-bold sm:text-2xl lg:text-3xl">
              اختَر مرحلتك الدراسية
            </h1>
            <p className="mt-2 text-sm text-slate-500 sm:text-base">
              اضغط على المرحلة لعرض الكورسات
            </p>
          </ScrollReveal>

          {loadingGrades ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-white"
                />
              ))}
            </div>
          ) : grades.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {grades.map((grade, index) => (
                <ScrollReveal key={grade.gradeId || index} variant="up" delay={index * 70}>
                  <button
                    type="button"
                    onClick={() => handleGradeClick(grade)}
                    className="group relative flex min-h-[7.5rem] w-full flex-col justify-between overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 text-right shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold-400/50 hover:shadow-lg hover:shadow-[#001848]/10 sm:min-h-[8rem] sm:p-6"
                  >
                    <span className="absolute inset-y-0 end-0 w-1 bg-gradient-to-b from-gold-400 to-[#001848] opacity-80 transition-all group-hover:w-1.5" />
                    <div className="flex items-start justify-between gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#001848] text-white transition-colors group-hover:bg-gold-500">
                        <GraduationCap className="h-5 w-5" />
                      </span>
                      <ChevronLeft className="mt-1 h-5 w-5 text-slate-300 transition-colors group-hover:text-[#001848]" />
                    </div>
                    <h3 className="mt-4 line-clamp-2 text-base font-bold leading-snug text-[#001848] font-cairo-bold sm:text-lg">
                      {grade.gradeName}
                    </h3>
                  </button>
                </ScrollReveal>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-12 text-center shadow-sm sm:py-16">
              <GraduationCap className="mx-auto mb-3 h-12 w-12 text-slate-300 sm:h-14 sm:w-14" />
              <p className="text-sm font-medium text-slate-600 sm:text-base">
                لا توجد مراحل دراسية متاحة لهذا المدرس حالياً
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default TeacherPage;
